import {Component, ReactNode} from 'react';
import {analytics} from '../../services/analytics';

/**
 * Global render-error safety net.
 *
 * Every route in App.tsx is lazy-loaded, and Firebase Hosting only serves the
 * CURRENT release's hashed chunk files. A tab opened before a deploy 404s the
 * moment it lazy-loads a route chunk ("Importing a module script failed." in
 * Safari, "Failed to fetch dynamically imported module" in Chrome). Without a
 * boundary, React unmounts the entire tree — the "white screen" incident.
 *
 * Recovery strategy, mirroring firestoreRecovery's reload guard:
 *   - Stale-chunk errors reload the page ONCE (the fresh index.html resolves
 *     the new hashed chunks). A sessionStorage timestamp guards against
 *     reload loops when the network itself is down.
 *   - Everything else (and a second chunk failure inside the guard window)
 *     renders a branded fallback with a manual reload button instead of a
 *     blank page.
 *
 * The fallback uses plain elements + inline styles so it renders even when
 * the error originated inside the Chakra/theme layer.
 */

const CHUNK_LOAD_ERROR_RE =
  /Importing a module script failed|dynamically imported module|Unable to preload CSS|ChunkLoadError|Loading chunk .* failed/i;

const RELOAD_GUARD_KEY = 'opalite_chunk_recovery_reload_at';
const RELOAD_MIN_INTERVAL_MS = 60_000;

function recentlyReloaded(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) || 0);
    return Number.isFinite(last) && Date.now() - last < RELOAD_MIN_INTERVAL_MS;
  } catch {
    return false;
  }
}

function markReloaded(): void {
  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    // sessionStorage unavailable (private mode / blocked) — best-effort only.
  }
}

export function isStaleChunkError(error: unknown): boolean {
  const message =
    (error as {message?: unknown})?.message ?? (error ? String(error) : '');
  return typeof message === 'string' && CHUNK_LOAD_ERROR_RE.test(message);
}

/**
 * Reload once to pick up a fresh index.html after a deploy invalidated the
 * running bundle's chunk URLs. Returns true when the reload was scheduled.
 */
export function reloadOnceForStaleChunk(source: string): boolean {
  if (recentlyReloaded()) return false;
  markReloaded();
  analytics.capture('stale_chunk_reload', {
    source,
    path: window.location.pathname,
  });
  // Defer so the telemetry event has a chance to flush before navigation.
  window.setTimeout(() => window.location.reload(), 150);
  return true;
}

/**
 * Vite dispatches `vite:preloadError` when the preload helper fails to fetch
 * a dynamic import's dependency (chunk or CSS). Installed once at boot;
 * preventDefault() suppresses the throw when we recover by reloading.
 */
export function installPreloadErrorRecovery(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('vite:preloadError', event => {
    if (reloadOnceForStaleChunk('vite_preload')) {
      event.preventDefault();
    }
  });
}

interface AppErrorBoundaryState {
  hasError: boolean;
  isReloading: boolean;
}

export default class AppErrorBoundary extends Component<
  {children: ReactNode},
  AppErrorBoundaryState
> {
  state: AppErrorBoundaryState = {hasError: false, isReloading: false};

  static getDerivedStateFromError(): Partial<AppErrorBoundaryState> {
    return {hasError: true};
  }

  componentDidCatch(error: Error) {
    const staleChunk = isStaleChunkError(error);

    try {
      analytics.capture('frontend_render_error', {
        error_message: error?.message || String(error),
        stale_chunk: staleChunk,
        path: window.location.pathname,
      });
    } catch {
      /* best-effort telemetry */
    }

    if (staleChunk && reloadOnceForStaleChunk('error_boundary')) {
      // Keep showing the quiet reloading state instead of the error card for
      // the fraction of a second before the reload lands.
      this.setState({isReloading: true});
    }
  }

  handleReload = () => {
    markReloaded();
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.state.isReloading) {
      return null;
    }

    return (
      <div
        role="alert"
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          background: '#F7FAFC',
          fontFamily: "'Figtree', sans-serif",
          color: '#1A3A4A',
        }}
      >
        <div
          style={{
            maxWidth: '420px',
            width: '100%',
            background: '#FFFFFF',
            border: '1px solid #E5E7EB',
            borderRadius: '16px',
            boxShadow:
              '0 1px 2px rgba(26, 58, 74, 0.04), 0 12px 28px -14px rgba(26, 58, 74, 0.16)',
            padding: '32px 28px',
            textAlign: 'center',
          }}
        >
          <div style={{fontSize: '19px', fontWeight: 600, marginBottom: '8px'}}>
            Something went wrong
          </div>
          <div
            style={{
              fontSize: '15px',
              lineHeight: 1.5,
              color: '#6B7280',
              marginBottom: '20px',
            }}
          >
            An unexpected error interrupted this view. Reloading usually fixes
            it — your recordings and notes are saved on our servers.
          </div>
          <button
            type="button"
            onClick={this.handleReload}
            style={{
              appearance: 'none',
              border: 'none',
              borderRadius: '10px',
              background: '#1E9FD4',
              color: '#FFFFFF',
              fontFamily: 'inherit',
              fontSize: '15px',
              fontWeight: 600,
              padding: '12px 28px',
              minHeight: '44px',
              cursor: 'pointer',
            }}
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}
